import React from 'react';
import {RootLayout} from './_react_/layouts/RootLayout';
import {TechseeRoute, RouteErrorBoundary} from './_react_/router/routing.infra';
import {guestRouteLoader, unknownRouteLoader} from './_react_/services/auth/auth-handler.service';

/**
 * Central routing configuration with declarative authentication
 * authMethod:
 *   - undefined: Protected route (default)
 *   - true: Explicitly protected
 *   - false: No authentication
 *   - function: Custom auth logic
 */
export const techseeRoutes: TechseeRoute[] = [
    {
        path: '/',
        element: <RootLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                index: true,
                loader: unknownRouteLoader,
                authMethod: false
            },
            // ============================================
            // AUTHENTICATION & ACCESS (no auth)
            // ============================================
            {
                path: 'login',
                authMethod: false,
                lazy: () => import('./_react_/states/login/login.module')
            },
            {
                path: 'login-callback',
                authMethod: false,
                lazy: () => import('./_react_/states/login-callback/login-callback.module')
            },
            {
                path: 'changePassword',
                authMethod: true,
                lazy: () => import('./_react_/states/change-password/change-password-routable.module')
            },
            {
                path: 'browser-not-supported',
                authMethod: false,
                lazy: () => import('./_react_/states/browser-not-supported/browser-not-supported-routable.module')
            },
            // ============================================
            // GUEST ACCESS (custom auth - requires observe=guest)
            // ============================================
            {
                path: 'guest',
                authMethod: false,
                lazy: () => import('./_react_/states/guest/guest-routable.module')
            },
            // ============================================
            // SSO ACTIONS (no auth)
            // ============================================
            {
                path: 'reactivate-account',
                authMethod: false,
                lazy: () => import('./_react_/states/sso-action/reactivate-account-routable.module')
            },
            {
                path: 'revoke-account',
                authMethod: false,
                lazy: () => import('./_react_/states/sso-action/revoke-account-routable.module')
            },
            // ============================================
            // PROTECTED ROUTES (default auth)
            // ============================================
            {
                path: 'dashboard',
                // authMethod: undefined → defaults to protectedRouteLoader
                lazy: () => import('./_react_/states/dashboard/dashboard.module'),
                children: [
                    {
                        path: 'entry',
                        lazy: () => import('./_react_/states/dashboard/entry/entry-routable.module')
                    },
                    {
                        path: 'main',
                        lazy: () => import('./_react_/states/dashboard/main/main.module')
                    },
                    {
                        path: 'store',
                        lazy: () => import('./_react_/states/dashboard/store/store.module')
                    }
                ]
            },
            {
                path: 'multipartyDashboard',
                lazy: () => import('./_react_/states/multipartyDashboard/multipartyDashboard-routable.module')
            },
            {
                path: 'emb-dashboard-error',
                authMethod: false,
                lazy: () =>
                    import('./_react_/states/embedded-dashboard-general-error/embedded-dashboard-general-error-routable.module')
            },
            {
                path: 'nway',
                authMethod: false,
                lazy: () => import('./_react_/states/participant/participant-routable.module')
            },
            {
                path: 'invite',
                authMethod: false,
                lazy: () => import('./_react_/states/invite/invite.module')
            },
            {
                path: 'join',
                lazy: () => import('./_react_/states/join/join-routable.module')
            },
            {
                path: 'session-ended',
                authMethod: false,
                lazy: () => import('./_react_/states/session-ended/session-ended-routable.module')
            },
            {
                path: 'invitation-ended',
                authMethod: false,
                lazy: () => import('./_react_/states/invitation-ended/invitation-ended-routable.module')
            },
            {
                path: 'history',
                authMethod: true,
                lazy: () => import('./_react_/states/history/history-routable.module')
            },
            {
                path: 'vjHistory',
                authMethod: true,
                lazy: () => import('./_react_/states/vj-history/vj-history-routable.module')
            },
            {
                path: 'wifiAnalyzerHistory',
                authMethod: true,
                lazy: () => import('./_react_/states/wifi-analyzer-history/wifi-analyzer-history-routable.module')
            },
            {
                path: 'historyConsole',
                authMethod: true,
                lazy: () => import('./_react_/states/history-console/history-console.module')
            },
            {
                path: 'connectivityCheck',
                authMethod: true,
                lazy: () => import('./_react_/states/connectivity/connectivity-check.module')
            },
            {
                path: 'connectivity-self-check',
                authMethod: true,
                lazy: () => import('./_react_/states/connectivity-standalone/connectivity-standalone-routable.module')
            },
            {
                path: 'connectivity-guru-data',
                lazy: () => import('./_react_/states/connectivity-guru-data/connectivity-guru-data.module')
            },
            // ============================================
            // OAUTH CALLBACK (custom auth via unknownRouteLoader)
            // ============================================
            {
                path: 'oauth/callback',
                authMethod: unknownRouteLoader,
                lazy: () => import('./_react_/states/oauth-callback/oauth-callback.module')
            },
            // ============================================
            // CATCH-ALL ROUTE (must be last)
            // ============================================
            {
                path: '*',
                loader: unknownRouteLoader,
                authMethod: false
            }
        ]
    }
];

// Export raw routes - processing happens in router.provider.tsx
export const appRoutes = techseeRoutes;
